"use client";
import React, { useState } from 'react';
import AppLayout from "@/components/layout/AppLayout";
import Link from "next/link";
import { ProductSchema, FAQSchema } from '@/components/seo/SchemaMarkup';
import SEOLinks from '@/components/seo/SEOLinks';
import siteConfig from '@/config/siteConfig';

export default function PricingPage() {
  const [pricingModel, setPricingModel] = useState<"combined" | "transactionOnly">("combined");

  const plans = [
    {
      name: "Pro Suite",
      description: "For small businesses",
      transactionFeeCombined: "2.5%",
      subscriptionFee: "€100",
      transactionFeeOnly: "5%",
      features: [
        "1 E-commerce Store",
        "1 Loyalty Program",
        "Polygon Blockchain",
        "Customer Analytics & Insights",
        "Up to 3 Admins",
      ],
      excludedFeatures: [
        "API Access",
        "Advanced Analytics with Smart Insights",
      ],
      cta: "Get Started",
      ctaLink: "/contact/schedule-demo",
      popular: false,
    },
    {
      name: "Growth",
      description: "For medium businesses",
      transactionFeeCombined: "1%",
      subscriptionFee: "€1,000",
      transactionFeeOnly: "2.5%",
      features: [
        "Up to 10 Stores",
        "Up to 10 Loyalty Programs",
        "Polygon or Ethereum",
        "Advanced Analytics",
        "Smart Insights",
        "API Access",
        "Beta Feature Access",
        "Up to 25 Admins",
      ],
      excludedFeatures: [],
      cta: "Get Started",
      ctaLink: "/contact/schedule-demo",
      popular: false,
    },
    {
      name: "Advanced",
      description: "For large businesses",
      transactionFeeCombined: "2%",
      subscriptionFee: "€3,000",
      transactionFeeOnly: "3.5%",
      features: [
        "Unlimited Stores",
        "Unlimited Loyalty Programs",
        "Any Blockchain",
        "Advanced Analytics",
        "Smart Insights",
        "API Access",
        "Beta Feature Access",
        "Unlimited Admins",
        "Priority Support",
      ],
      excludedFeatures: [],
      cta: "Get Started",
      ctaLink: "/contact/schedule-demo",
      popular: false,
    },
    {
      name: "Marketplace",
      description: "For marketplace operators",
      transactionFeeCombined: "2.5%",
      subscriptionFee: "€5,000",
      transactionFeeOnly: "5%",
      features: [
        "Full Marketplace",
        "Polygon or Ethereum",
        "Advanced Analytics",
        "Smart Insights",
        "API Access",
        "Beta Feature Access",
        "Unlimited Admins",
        "Priority Support",
        "Multi-vendor Support",
      ],
      excludedFeatures: [],
      cta: "Get Started",
      ctaLink: "/contact/schedule-demo",
      popular: false,
    },
  ];

  const faqQuestions = [
    {
      question: "What's the difference between combined and transaction-only pricing?",
      answer: "Combined pricing includes a monthly subscription fee plus a lower transaction fee, while transaction-only pricing has no monthly fee but higher transaction fees. Choose based on your expected transaction volume."
    },
    {
      question: "Are there additional payment provider fees?",
      answer: "Yes, payment provider fees come on top of our fees. These vary depending on the payment provider you choose to integrate with your Perxify store or marketplace."
    },
    {
      question: "Are there any setup fees?",
      answer: "No, there are no setup fees for any of our plans. You can get started immediately after signing up."
    },
    {
      question: "Can I change my plan later?",
      answer: "Yes, you can upgrade or downgrade your plan at any time. Changes take effect at the start of your next billing cycle."
    },
    {
      question: "What kind of support is included?",
      answer: "All plans include email support to help you maximize your platform investment. Advanced and Marketplace plans include priority support with faster response times, ensuring your business operations run smoothly as you scale."
    },
    {
      question: "What is included in the Enterprise plan?",
      answer: "The Enterprise plan offers completely custom pricing and features tailored to your specific business needs. This includes custom development, dedicated support, and flexible pricing structures. Contact our sales team for a personalized Enterprise quote."
    }
  ];

  return (
    <>
      <meta name="author" content={siteConfig.authors} />
      <meta property="article:published_time" content={siteConfig.publishedTime} />
      <meta property="fb:app_id" content={siteConfig.FacebookAppID} />
      <ProductSchema 
        name="Perxify Commerce Cloud Plans"
        description="Flexible pricing plans for Perxify's Commerce Cloud, tailored for small to large businesses and marketplaces"
        image="${siteConfig.assetUrl}/images/pricing-overview.jpg"
        offers={[
          {
            id: "pro-suite",
            name: "Pro Suite",
            description: "For small businesses",
            price: pricingModel === "combined" ? "2.5% transaction fee + €100/month" : "5% transaction fee"
          },
          {
            id: "growth",
            name: "Growth",
            description: "For medium businesses",
            price: pricingModel === "combined" ? "1% transaction fee + €1,000/month" : "2.5% transaction fee"
          },
          {
            id: "advanced",
            name: "Advanced",
            description: "For large businesses",
            price: pricingModel === "combined" ? "2% transaction fee + €3,000/month" : "3.5% transaction fee"
          },
          {
            id: "marketplace",
            name: "Marketplace",
            description: "For open marketplaces",
            price: pricingModel === "combined" ? "2.5% transaction fee + €5,000/month" : "5% transaction fee"
          },
          {
            id: "enterprise",
            name: "Enterprise",
            description: "Custom solutions for large organizations",
            price: "Custom pricing"
          }
        ]}
      />
      <FAQSchema questions={faqQuestions} />
      <SEOLinks customPath="/pricing" />
      <AppLayout>
        {/* Hero Section */}
        <section className="gradient-bg text-white">
          <div className="container py-16 md:py-24">
            <div className="text-center">
              <h1 className="text-white mb-6">
                Simple, Transparent Pricing
              </h1>
              <p className="text-lg mb-8 text-gray-100 max-w-3xl mx-auto">
                Choose the plan that fits your business needs. Our flexible pricing scales with your success, ensuring you only pay more as you grow and generate more revenue.
              </p>
              {/* Pricing Toggle */}
              <div className="flex max-[559px]:flex-col max-[559px]:space-y-2 max-[559px]:items-center max-[559px]:text-center justify-center items-center space-x-4 mb-8">
                <span className={`text-lg ${pricingModel === "combined" ? "text-white font-semibold" : "text-gray-300"}`}>Transaction + Subscription Fee</span>
                <button
                  onClick={() => setPricingModel(pricingModel === "combined" ? "transactionOnly" : "combined")}
                  className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors duration-200 ease-in-out ${pricingModel === "transactionOnly" ? "bg-[#00A4BD]" : "bg-gray-400"}`}
                >
                  <span
                    className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform duration-200 ease-in-out ${pricingModel === "transactionOnly" ? "translate-x-6" : "translate-x-1"}`}
                  />
                </button>
                <span className={`text-lg ${pricingModel === "transactionOnly" ? "text-white font-semibold" : "text-gray-300"}`}>Transaction Fee Only</span>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Plans Section */}
        <section className="section bg-white">
          <div className="container">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {plans.map((plan, index) => (
                <div key={index} className={`relative ${plan.popular ? "pt-8" : ""}`}>
                  {plan.popular && (
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-4 bg-[#00A4BD] text-white text-center py-1 px-4 rounded-full text-sm font-medium shadow-md z-10">
                      MOST POPULAR
                    </div>
                  )}
                  <div className={`card border ${plan.popular ? "border-[#00A4BD] border-2" : "border-gray-200"} rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300 h-full flex flex-col`}>
                    <div className="p-6 flex-grow">
                      <h3 className="text-xl font-semibold mb-2">{plan.name}</h3>
                      <p className="text-gray-600 mb-6">{plan.description}</p>
                      
                      {/* Pricing Display based on Toggle */}
                      <div className="mb-4 min-h-[80px]">
                        {pricingModel === "combined" ? (
                          <>
                            <p className="font-bold text-lg text-gray-800">{plan.transactionFeeCombined} transaction fee</p>
                            <p className="text-gray-700">+ {plan.subscriptionFee}/month subscription fee</p>
                          </>
                        ) : (
                          <>
                            <p className="font-bold text-lg text-gray-800">{plan.transactionFeeOnly} transaction fee</p>
                            <p className="text-gray-700">(€0 subscription fee)</p>
                          </>
                        )}
                      </div>
                      
                      <ul className="space-y-2 mb-8">
                        {plan.features.map((feature, fIndex) => (
                          <li key={fIndex} className="flex items-start">
                            <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span className="text-sm">{feature}</span>
                          </li>
                        ))}
                        {plan.excludedFeatures && plan.excludedFeatures.map((feature, fIndex) => (
                          <li key={`ex-${fIndex}`} className="flex items-start text-gray-400">
                            <svg className="h-5 w-5 text-gray-300 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            <span className="text-sm">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="p-6 pt-0 mt-auto">
                      <Link href={plan.ctaLink} className="btn-primary w-full text-center">
                        {plan.cta}
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Payment Provider Note */}
            <div className="text-center mt-12">
              <p className="text-sm text-gray-500 max-w-3xl mx-auto">
                Note: Payment provider fees are additional to Perxify fees. Shared kickback on delivery and "buy now, pay later" fees available on larger volumes.
              </p>
            </div>

            {/* Enterprise Plan */}
            <div className="mt-16 bg-gray-50 rounded-lg p-8 text-center">
              <h3 className="text-2xl font-semibold mb-4">Enterprise</h3>
              <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                Need a custom solution? We offer enterprise plans with dedicated support, custom integrations, and volume discounts.
              </p>
              <Link href="/contact/schedule-demo" className="btn-primary inline-block">
                Contact Sales
              </Link>
            </div>

            {/* FAQ Section */}
            <div className="mt-24">
              <h2 className="text-2xl font-semibold mb-8 text-center">Frequently Asked Questions</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="card p-6 border border-gray-200 rounded-lg">
                  <h3 className="text-lg font-medium mb-3">What's the difference between combined and transaction-only pricing?</h3>
                  <p className="text-gray-600">
                    Combined pricing includes a monthly subscription fee plus a lower transaction fee, while transaction-only pricing has no monthly fee but higher transaction fees. Choose based on your expected transaction volume.
                  </p>
                </div>
                <div className="card p-6 border border-gray-200 rounded-lg">
                  <h3 className="text-lg font-medium mb-3">Are there additional payment provider fees?</h3>
                  <p className="text-gray-600">
                    Yes, payment provider fees come on top of our fees. These vary depending on the payment provider you choose to integrate with your Perxify store or marketplace.
                  </p>
                </div>
                <div className="card p-6 border border-gray-200 rounded-lg">
                  <h3 className="text-lg font-medium mb-3">Are there any setup fees?</h3>
                  <p className="text-gray-600">
                    No, there are no setup fees for any of our plans. You can get started immediately after signing up.
                  </p>
                </div>
                <div className="card p-6 border border-gray-200 rounded-lg">
                  <h3 className="text-lg font-medium mb-3">Can I change my plan later?</h3>
                  <p className="text-gray-600">
                    Yes, you can upgrade or downgrade your plan at any time. Changes take effect at the start of your next billing cycle.
                  </p>
                </div>
                <div className="card p-6 border border-gray-200 rounded-lg">
                  <h3 className="text-lg font-medium mb-3">What kind of support is included?</h3>
                  <p className="text-gray-600">
                    All plans include email support to help you maximize your platform investment. Advanced and Marketplace plans include priority support with faster response times, ensuring your business operations run smoothly as you scale.
                  </p>
                </div>
                <div className="card p-6 border border-gray-200 rounded-lg">
                  <h3 className="text-lg font-medium mb-3">What is included in the Enterprise plan?</h3>
                  <p className="text-gray-600">
                    The Enterprise plan offers completely custom pricing and features tailored to your specific business needs. This includes custom development, dedicated support, and flexible pricing structures. Contact our sales team for a personalized Enterprise quote.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </AppLayout>
    </>
  );
}